import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from 'oidc-client-ts';
import { userManager } from './oidc';
import { createApiClient, type ApiClient } from '../api/client';
import { env } from '../env';

export interface AuthState {
  status: 'loading' | 'signedOut' | 'signedIn';
  user: User | null;
  api: ApiClient;
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

// Exported for the fixture provider (src/fixtures), which supplies a canned session.
export const AuthContext = createContext<AuthState | null>(null);

// Module-level so the API client can read the token without React state and without being
// re-created on every render (which would restart polling effects).
let currentUser: User | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  const apply = useCallback((u: User | null) => {
    const valid = u && !u.expired ? u : null;
    currentUser = valid;
    setUser(valid);
    setStatus(valid ? 'signedIn' : 'signedOut');
  }, []);

  useEffect(() => {
    let cancelled = false;
    void userManager.getUser().then((u) => {
      if (!cancelled) apply(u);
    });
    const onLoaded = (u: User) => apply(u);
    const onGone = () => apply(null);
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onGone);
    userManager.events.addAccessTokenExpired(onGone);
    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onGone);
      userManager.events.removeAccessTokenExpired(onGone);
    };
  }, [apply]);

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: env.vpnApiBase,
        getToken: () => (currentUser && !currentUser.expired ? currentUser.access_token : null),
        // The server rejected the token (revoked / expired early): drop it so the UI offers sign-in.
        onUnauthorized: () => {
          void userManager.removeUser();
        },
      }),
    [],
  );

  const signIn = useCallback(
    (returnTo?: string) => userManager.signinRedirect({ state: { returnTo: returnTo ?? '/' } }),
    [],
  );
  const signOut = useCallback(() => userManager.signoutRedirect(), []);

  const value = useMemo(() => ({ status, user, api, signIn, signOut }), [status, user, api, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
